import { useState, useCallback } from "react"
import {
  getStudioDesigns,
  getUserStudios,
  getStudioMembersWithDesigns,
  setPreferredDesign as setPreferredDesignUtil,
  createNotification,
  STUDIO_ROLES,
} from "../utils/firebaseUtils"

/**
 * Hook to manage studio operations
 * Handles: loading studios, designs, members, and setting preferred designs
 * @param {Object} currentUser - Current Firebase user
 * @param {Function} generateThumbnail - Function to generate design thumbnails
 * @param {Object} designThumbnails - Current design thumbnails cache
 * @returns {Object} Studio management state and functions
 */
export function useStudioManagement(currentUser, generateThumbnail, designThumbnails) {
  const [userStudios, setUserStudios] = useState([])
  const [selectedStudio, setSelectedStudio] = useState(null)
  const [studioDesigns, setStudioDesigns] = useState([])
  const [designContext, setDesignContext] = useState("personal") // "personal" or "studio"
  const [studioMembersWithDesigns, setStudioMembersWithDesigns] = useState([])

  // Load studio designs
  const loadStudioDesigns = useCallback(
    async studioId => {
      if (!currentUser || !studioId) return

      try {
        const designs = await getStudioDesigns(studioId, currentUser.uid)
        setStudioDesigns(designs)

        // Generate thumbnails for studio designs
        for (const design of designs) {
          if (!designThumbnails[design.id]) {
            await generateThumbnail(design)
          }
        }
      } catch (err) {
        console.error("Error loading studio designs:", err.message)
      }
    },
    [currentUser, designThumbnails, generateThumbnail],
  )

  const hasInitialized = useState({ current: false })[0]

  // Load user's studios
  const loadUserStudios = useCallback(
    async (loadDesignsForFirstStudio = true) => {
      if (!currentUser) return

      try {
        const studios = await getUserStudios(currentUser.uid)
        setUserStudios(studios)

        // Only auto-select studio context on first load, not on refreshes
        if (!hasInitialized.current && studios.length > 0 && loadDesignsForFirstStudio) {
          setDesignContext("studio")
          setSelectedStudio(studios[0])
          await loadStudioDesigns(studios[0].id)
          hasInitialized.current = true
        } else if (!hasInitialized.current && studios.length === 0) {
          setDesignContext("personal")
          setSelectedStudio(null)
          hasInitialized.current = true
        }
      } catch (err) {
        console.error("Error loading studios:", err.message)
      }
    },
    [currentUser, loadStudioDesigns, hasInitialized],
  )

  // Load studio members with their designs
  const loadStudioMembersWithDesigns = useCallback(
    async studioId => {
      if (!currentUser || !studioId) return

      try {
        const membersWithDesigns = await getStudioMembersWithDesigns(studioId, currentUser.uid)
        setStudioMembersWithDesigns(membersWithDesigns)

        // Generate thumbnails for all member designs
        for (const member of membersWithDesigns) {
          for (const design of member.designs) {
            if (!designThumbnails[design.id]) {
              await generateThumbnail(design)
            }
          }
        }
      } catch (err) {
        console.error("Error loading studio members with designs:", err.message)
      }
    },
    [currentUser, designThumbnails, generateThumbnail],
  )

  // Set preferred design for current user
  const handleSetPreferredDesign = useCallback(
    async designId => {
      if (!currentUser) return

      try {
        const result = await setPreferredDesignUtil(designId, currentUser.uid)

        // Only notify the studio owner when submitting (not when un-submitting)
        if (result?.action === "submitted" && selectedStudio && selectedStudio.ownerId && currentUser) {
          try {
            const design = studioDesigns.find(d => d.id === designId)
            const designName = design?.name || "Untitled Design"
            const memberName = currentUser.displayName || currentUser.email || "A member"

            await createNotification(selectedStudio.ownerId, {
              type: "ad_submission",
              title: "New Ad Submitted",
              message: `${memberName} submitted "${designName}" to ${selectedStudio.name}`,
              studioId: selectedStudio.id,
              designId: designId,
              fromUserId: currentUser.uid,
            })
          } catch (err) {
            // Don't block the submission if notification fails
            console.error("Failed to create notification:", err)
          }
        }

        // Refresh studio designs
        if (selectedStudio) {
          await loadStudioDesigns(selectedStudio.id)
        }
        return result
      } catch (err) {
        console.error("Error setting preferred design:", err.message)
        return false
      }
    },
    [currentUser, selectedStudio, studioDesigns, loadStudioDesigns],
  )

  // Check if current user is admin in selected studio
  const isCurrentUserStudioAdmin = useCallback(() => {
    if (!selectedStudio) return false
    return selectedStudio.userRole === STUDIO_ROLES.OWNER || selectedStudio.userRole === STUDIO_ROLES.ADMIN
  }, [selectedStudio])

  return {
    userStudios,
    setUserStudios,
    selectedStudio,
    setSelectedStudio,
    studioDesigns,
    setStudioDesigns,
    designContext,
    setDesignContext,
    studioMembersWithDesigns,
    setStudioMembersWithDesigns,
    loadUserStudios,
    loadStudioDesigns,
    loadStudioMembersWithDesigns,
    handleSetPreferredDesign,
    isCurrentUserStudioAdmin,
  }
}
